// src/pages/pmstudio/PMStudioEditor.jsx

import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Info, CheckCircle2, AlertTriangle, Sparkles, TrendingUp, AlertCircle } from "lucide-react";

import Main from "../Main";
import { makeCompanyNav } from "../nav";

import Page from "./blocks/Page.jsx";
import IMSectionsSidebar from "./components/IMSectionsSidebar.jsx";
import { composePresentationRows, hasRenderableSectionBody } from "./pmTemplateComposition";

import { API_BASE, getCsrfToken } from "../../services/server";

import "./css/pm-studio-editor.css";
import "../css/pmstudio.css";

const CALLOUT_ICONS = {
  info:     Info,
  check:    CheckCircle2,
  alert:    AlertTriangle,
  sparkles: Sparkles,
  trend:    TrendingUp,
  warning:  AlertCircle,
};

/* ======================================================================= */
/* INLINE API (no pmstudioApi.js import)                                   */
/* ======================================================================= */

async function apiFetch(path, { method = "GET", body } = {}) {
  const csrf = getCsrfToken();
  const url = `${API_BASE}${path}`;

  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: {
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(csrf ? { "X-CSRFToken": csrf } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("text/html")) {
    const text = await res.text().catch(() => "");
    throw new Error(`Expected JSON but got HTML (${res.status}). ${text.slice(0, 200)}`);
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || data?.error || `HTTP ${res.status}`);
  return data;
}

/**
 * Replace these paths to match your backend.
 * - fetchIMFull(orgnr) should return { company|organisation, pm, templates?, ... }
 * - saveIMTemplate(orgnr, payload) should save a template for the org/workspace.
 */
async function fetchIMFull(orgnr) {
  return apiFetch(`/im-studio/company/${encodeURIComponent(orgnr)}/full/`);
}

async function saveIMTemplate(orgnr, payload) {
  return apiFetch(`/im-studio/company/${encodeURIComponent(orgnr)}/templates/`, {
    method: "POST",
    body: payload,
  });
}

async function saveGlobalIMTemplate(payload) {
  return apiFetch(`/im-studio/global-templates/`, {
    method: "POST",
    body: payload,
  });
}

/* ======================================================================= */
/* IM-SEKTIONER – samma lista som i sidopanelen                            */
/* ======================================================================= */

const IM_SECTIONS = [
  { id: "summary", label: "1. Bolagsöversikt" },
  { id: "owners", label: "2. Ägare & bakgrund" },
  { id: "market", label: "3. Marknad & kunder" },
  { id: "offering", label: "4. Erbjudande & tjänster" },
  { id: "business_model", label: "5. Affärsmodell & intäkter" },
  { id: "organisation", label: "6. Organisation & personal" },
  { id: "financials", label: "7. Finansiell utveckling" },
  { id: "valuation", label: "8. Värdering" },
  { id: "risks", label: "9. Nyckelrisker" },
  { id: "uplift", label: "10. Value uplift & möjligheter" },
  { id: "process", label: "11. Process & nästa steg" },
];

const LABEL_BY_ID = IM_SECTIONS.reduce((acc, s) => {
  acc[s.id] = s.label;
  return acc;
}, {});

/* ======================================================================= */
/* ID-GENERATOR + LAYOUT-HELPERS                                           */
/* ======================================================================= */

let idCounter = 1;
const newId = (prefix) => `${prefix}-${idCounter++}`;

function createStructureRow(layout, initialItems = []) {
  if (layout === "1col") {
    return {
      id: newId("row"),
      kind: "structure",
      layout: "1col",
      bgColor: "",
      styles: {},
      cells: [{ id: newId("cell"), items: initialItems }],
    };
  }

  if (layout === "1-2") {
    return {
      id: newId("row"),
      kind: "structure",
      layout: "1-2",
      bgColor: "",
      styles: {},
      cells: [
        { id: newId("cell"), items: [] },
        { id: newId("cell"), items: initialItems },
      ],
    };
  }

  return null;
}

function createSectionItem(sectionId) {
  return { id: newId("item"), type: "section", sectionId, styles: {} };
}

function createTextItem() {
  return { id: newId("item"), type: "text", styles: {} };
}

function createImageItem(imageKey = "main") {
  return { id: newId("item"), type: "image", imageKey, styles: {} };
}

function createInlineDividerItem() {
  return { id: newId("item"), type: "inline-divider", styles: {} };
}

function createCalloutItem(variant = "info") {
  const defaultIconByVariant = {
    info: "info",
    success: "check",
    warning: "alert",
    accent: "sparkles",
  };
  return {
    id: newId("item"),
    type: "callout",
    variant,
    icon: defaultIconByVariant[variant] || "info",
    heading: "Rubrik",
    body: "Beskrivande text för den här rutan.",
    styles: {},
  };
}

function createQuoteItem() {
  return {
    id: newId("item"),
    type: "quote",
    body: "Ett citat eller en framträdande passage.",
    attribution: "",
    styles: {},
  };
}

function createKpiCardItem() {
  return {
    id: newId("item"),
    type: "kpi-card",
    label: "Nyckeltal",
    value: "—",
    change: "",
    styles: {},
  };
}

function createStatGridItem() {
  return {
    id: newId("item"),
    type: "stat-grid",
    cards: [
      { label: "Omsättning", value: "—", change: "" },
      { label: "EBITDA", value: "—", change: "" },
      { label: "Tillväxt", value: "—", change: "" },
    ],
    styles: {},
  };
}

function createImageCaptionItem(imageKey = "main") {
  return {
    id: newId("item"),
    type: "image-caption",
    imageKey,
    title: "Bildrubrik",
    caption: "Bildtext",
    styles: {},
  };
}

function createDividerRow() {
  return { id: newId("row"), kind: "divider" };
}

function createPageBreakRow() {
  return { id: newId("row"), kind: "pagebreak" };
}

/* ======================================================================= */
/* BLOCK-KOMPONENTER                                                       */
/* ======================================================================= */

function SectionBlock({ sectionId, pm, style }) {
  const titles = pm?.titles || {};
  const sections = pm?.sections || {};

  const heading = titles[sectionId] || LABEL_BY_ID[sectionId] || "";
  const rawBody = sections[sectionId] || "";
  const body = rawBody;
  if (!hasRenderableSectionBody(body)) return null;

  return (
    <section className="pmstudio-section-block" style={style}>
      {heading && (
        <div className="pmstudio-section-heading-row">
          <h2 className="pmstudio-section-heading">{heading}</h2>
        </div>
      )}
      {body ? (
        <p className="pmstudio-section-body">{body}</p>
      ) : null}
    </section>
  );
}

function TextBlock({ item, style }) {
  const body = item?.body || "";
  return (
    <div className="pmstudio-textblock" style={style}>
      {body || (
        <span style={{ opacity: 0.55, fontStyle: "italic" }}>
          Textblock – klicka för att redigera.
        </span>
      )}
    </div>
  );
}

function ImageBlock({ pm, imageKey, item, style, onUpload }) {
  const images = pm?.images || {};

  // Logo keys should only show their own logo — no cross-fallback
  let src;
  if (imageKey === "broker_logo") {
    src = images.broker_logo || null;
  } else if (imageKey === "seller_logo") {
    src = images.seller_logo || images.logo || images.logo_light || null;
  } else {
    src = images[imageKey] || images.main || images.hero || images.logo || null;
  }

  const labelMap = {
    broker_logo: "Mäklarlogotyp",
    seller_logo: "Säljarens logotyp",
    cover: "Omslagsbild",
    logo: "Logotyp",
    main: "Huvudbild",
    hero: "Herobild",
    team: "Team- eller kontorsbild",
  };
  const labelText = item?.label || labelMap[imageKey] || "Bild";

  return (
    <div className="pmstudio-imageblock" style={style}>
      {src ? (
        <ImageWithReplace
          src={src}
          alt={labelText}
          imageKey={imageKey}
          onUpload={onUpload}
        />
      ) : (
        <ImageDropSlot label={labelText} imageKey={imageKey} onUpload={onUpload} />
      )}
    </div>
  );
}

function CalloutBlock({ item, style, onChange }) {
  const variant = item.variant || "info";
  const IconCmp = CALLOUT_ICONS[item.icon] || CALLOUT_ICONS.info;
  return (
    <div
      className={`pmstudio-callout pmstudio-callout--${variant}`}
      style={style}
    >
      <div className="pmstudio-callout-icon" aria-hidden="true">
        <IconCmp size={20} strokeWidth={2} />
      </div>
      <div className="pmstudio-callout-content">
        <div
          className="pmstudio-callout-heading"
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => onChange?.({ heading: e.currentTarget.textContent })}
        >
          {item.heading || "Rubrik"}
        </div>
        <div
          className="pmstudio-callout-body"
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => onChange?.({ body: e.currentTarget.textContent })}
        >
          {item.body || ""}
        </div>
      </div>
    </div>
  );
}

function QuoteBlock({ item, style, onChange }) {
  return (
    <blockquote className="pmstudio-quote" style={style}>
      <div
        className="pmstudio-quote-body"
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => onChange?.({ body: e.currentTarget.textContent })}
      >
        {item.body || "Citat…"}
      </div>
      <footer
        className="pmstudio-quote-attribution"
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => onChange?.({ attribution: e.currentTarget.textContent })}
      >
        {item.attribution || "— Källa"}
      </footer>
    </blockquote>
  );
}

function KpiCardBlock({ item, style, onChange }) {
  return (
    <div className="pmstudio-kpi-card" style={style}>
      <div
        className="pmstudio-kpi-label"
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => onChange?.({ label: e.currentTarget.textContent })}
      >
        {item.label || "Nyckeltal"}
      </div>
      <div
        className="pmstudio-kpi-value"
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => onChange?.({ value: e.currentTarget.textContent })}
      >
        {item.value || "—"}
      </div>
      {item.change !== undefined && (
        <div
          className="pmstudio-kpi-change"
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => onChange?.({ change: e.currentTarget.textContent })}
        >
          {item.change || ""}
        </div>
      )}
    </div>
  );
}

function StatGridBlock({ item, style, onChange }) {
  const cards = Array.isArray(item.cards) ? item.cards : [];
  const updateCard = (idx, patch) => {
    const next = cards.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    onChange?.({ cards: next });
  };
  return (
    <div className="pmstudio-stat-grid" style={style}>
      {cards.map((card, i) => (
        <div key={i} className="pmstudio-kpi-card">
          <div
            className="pmstudio-kpi-label"
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => updateCard(i, { label: e.currentTarget.textContent })}
          >
            {card.label || ""}
          </div>
          <div
            className="pmstudio-kpi-value"
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => updateCard(i, { value: e.currentTarget.textContent })}
          >
            {card.value || "—"}
          </div>
          {card.change !== undefined && (
            <div
              className="pmstudio-kpi-change"
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) => updateCard(i, { change: e.currentTarget.textContent })}
            >
              {card.change || ""}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ImageCaptionBlock({ item, pm, style, onChange, onUpload }) {
  const images = pm?.images || {};
  const imageKey = item.imageKey || "main";
  const src = images[imageKey] || images.main || images.hero || null;
  const labelText = item.label || "Bild — klicka för att ladda upp";
  return (
    <figure className="pmstudio-image-caption" style={style}>
      <h4
        className="pmstudio-image-caption-title"
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => onChange?.({ title: e.currentTarget.textContent })}
      >
        {item.title || "Bildrubrik"}
      </h4>
      {src ? (
        <ImageWithReplace
          src={src}
          alt={item.caption || ""}
          imageKey={imageKey}
          onUpload={onUpload}
          className="pmstudio-image-caption-img"
        />
      ) : (
        <ImageDropSlot label={labelText} imageKey={imageKey} onUpload={onUpload} variant="caption" />
      )}
      <figcaption
        className="pmstudio-image-caption-text"
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => onChange?.({ caption: e.currentTarget.textContent })}
      >
        {item.caption || "Bildtext"}
      </figcaption>
    </figure>
  );
}

/* ─── Image drop-slot + replace helpers ──────────────────────────────────── */

function ImageDropSlot({ label, imageKey, onUpload, variant }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handlePick = (e) => {
    e.stopPropagation();
    inputRef.current?.click();
  };
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onUpload) return;
    setBusy(true);
    try {
      const dataUrl = await readFileAsDataURL(file);
      await onUpload(imageKey, dataUrl);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <button
      type="button"
      className={
        "pmstudio-image-slot" +
        (variant === "caption" ? " pmstudio-image-slot--caption" : "")
      }
      onClick={handlePick}
      disabled={busy}
    >
      <div className="pmstudio-image-slot-icon" aria-hidden="true">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="9" cy="9" r="2" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
      </div>
      <div className="pmstudio-image-slot-label">{label}</div>
      <div className="pmstudio-image-slot-hint">
        {busy ? "Laddar upp…" : "Klicka för att ladda upp en bild"}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        style={{ display: "none" }}
      />
    </button>
  );
}

function ImageWithReplace({ src, alt, imageKey, onUpload, className }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handlePick = (e) => {
    e.stopPropagation();
    inputRef.current?.click();
  };
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onUpload) return;
    setBusy(true);
    try {
      const dataUrl = await readFileAsDataURL(file);
      await onUpload(imageKey, dataUrl);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="pmstudio-image-with-replace">
      <img src={src} alt={alt || ""} className={className || "pmstudio-imageblock-img"} />
      <button
        type="button"
        className="pmstudio-image-replace-btn"
        onClick={handlePick}
        disabled={busy}
      >
        {busy ? "Laddar upp…" : "Ersätt bild"}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        style={{ display: "none" }}
      />
    </div>
  );
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* ======================================================================= */
/* INNER EDITOR – CANVAS + HÖGERPANEL                                      */
/* ======================================================================= */

function themeToCssVars(theme) {
  if (!theme || typeof theme !== "object") return {};
  const vars = {};
  if (theme.primary_color)     vars["--im-primary"] = theme.primary_color;
  if (theme.accent_color)      vars["--im-accent"] = theme.accent_color;
  if (theme.surface_color)     vars["--im-surface"] = theme.surface_color;
  if (theme.muted_color)       vars["--im-muted"] = theme.muted_color;
  if (theme.text_color)        vars["--im-text"] = theme.text_color;
  if (theme.page_bg)           vars["--im-page-bg"] = theme.page_bg;
  if (theme.font_heading)      vars["--im-font-heading"] = theme.font_heading;
  if (theme.font_body)         vars["--im-font-body"] = theme.font_body;
  if (theme.heading_scale)     vars["--im-heading-scale"] = String(theme.heading_scale);
  if (theme.body_line_height)  vars["--im-body-line-height"] = String(theme.body_line_height);
  if (theme.row_gap)           vars["--im-row-gap"] = theme.row_gap;
  if (theme.callout_bg)        vars["--im-callout-bg"] = theme.callout_bg;
  if (theme.callout_border)    vars["--im-callout-border"] = theme.callout_border;
  if (theme.callout_accent)    vars["--im-callout-accent"] = theme.callout_accent;
  return vars;
}

async function saveIMImages(orgnr, images) {
  return apiFetch(`/im-studio/context/${encodeURIComponent(orgnr)}/images/`, {
    method: "POST",
    body: { images },
  });
}

function PMStudioEditorInner({ slug, full, setFull, loading, error, title, setTitle, rows, setRows, theme, setTheme }) {
  const previewRef = useRef(null);
  const [sidebarMode, setSidebarMode] = useState("content");

  const [selectedTarget, setSelectedTarget] = useState(null);
  const [dragSource, setDragSource] = useState(null);

  const selectedRow = useMemo(() => {
    if (!selectedTarget || selectedTarget.kind !== "row") return null;
    return rows.find((r) => r.id === selectedTarget.rowId) || null;
  }, [rows, selectedTarget]);

  const getSelectedStyles = () => {
    if (!selectedTarget) return {};

    if (selectedTarget.kind === "row") {
      const row = rows.find((r) => r.id === selectedTarget.rowId) || null;
      return row?.styles || {};
    }

    if (selectedTarget.kind === "item") {
      const row = rows.find((r) => r.id === selectedTarget.rowId);
      if (!row || row.kind !== "structure") return {};
      const cell = row.cells.find((c) => c.id === selectedTarget.cellId);
      if (!cell) return {};
      const item = cell.items.find((it) => it.id === selectedTarget.itemId);
      return item?.styles || {};
    }

    return {};
  };

  const activeStyles = getSelectedStyles();

  const applyStyleToSelected = (patch) => {
    if (!selectedTarget) return;

    setRows((prev) =>
      prev.map((row) => {
        if (selectedTarget.kind === "row") {
          if (row.id !== selectedTarget.rowId) return row;
          return { ...row, styles: { ...(row.styles || {}), ...patch } };
        }

        if (row.id !== selectedTarget.rowId || row.kind !== "structure") return row;

        const newCells = row.cells.map((cell) => {
          if (cell.id !== selectedTarget.cellId) return cell;
          const newItems = (cell.items || []).map((item) => {
            if (item.id !== selectedTarget.itemId) return item;
            return { ...item, styles: { ...(item.styles || {}), ...patch } };
          });
          return { ...cell, items: newItems };
        });

        return { ...row, cells: newCells };
      })
    );
  };

  const moveRow = (rowId, direction) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === rowId);
      if (idx === -1) return prev;
      const newIndex = direction === "up" ? idx - 1 : idx + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;
      const copy = prev.slice();
      const tmp = copy[idx];
      copy[idx] = copy[newIndex];
      copy[newIndex] = tmp;
      return copy;
    });
  };

  const deleteRow = (rowId) => {
    setRows((prev) => prev.filter((r) => r.id !== rowId));
    if (selectedTarget?.kind === "row" && selectedTarget.rowId === rowId) {
      setSelectedTarget(null);
    }
  };

  const handleSectionChange = (id, text, titleFromSidebar) => {
    setFull((prev) => {
      if (!prev) return prev;
      const pm = prev.pm || {};
      const currentSections = pm.sections || {};
      const currentTitles = pm.titles || {};

      return {
        ...prev,
        pm: {
          ...pm,
          sections: { ...currentSections, [id]: text },
          titles: {
            ...currentTitles,
            [id]: titleFromSidebar ?? currentTitles[id],
          },
          images: pm.images || {},
        },
      };
    });
  };

  const handleCanvasDragOver = (e) => {
    if (dragSource) e.preventDefault();
  };

  const handleRowDrop = (e, index) => {
    if (!dragSource) return;
    e.preventDefault();

    setRows((prev) => {
      const copy = prev.slice();

      if (dragSource.type === "im-section") {
        const newRow = createStructureRow("1col", [createSectionItem(dragSource.sectionId)]);
        copy.splice(index, 0, newRow);
      } else if (dragSource.type === "structure-1col") {
        copy.splice(index, 0, createStructureRow("1col", []));
      } else if (dragSource.type === "structure-1-2") {
        copy.splice(index, 0, createStructureRow("1-2", []));
      } else if (dragSource.type === "content-divider") {
        copy.splice(index, 0, createDividerRow());
      } else if (dragSource.type === "content-pagebreak") {
        copy.splice(index, 0, createPageBreakRow());
      } else if (dragSource.type === "content-text") {
        copy.splice(index, 0, createStructureRow("1col", [createTextItem()]));
      } else if (dragSource.type === "content-image") {
        copy.splice(index, 0, createStructureRow("1col", [createImageItem("main")]));
      } else if (dragSource.type === "content-callout") {
        copy.splice(index, 0, createStructureRow("1col", [createCalloutItem(dragSource.variant || "info")]));
      } else if (dragSource.type === "content-quote") {
        copy.splice(index, 0, createStructureRow("1col", [createQuoteItem()]));
      } else if (dragSource.type === "content-kpi") {
        copy.splice(index, 0, createStructureRow("1col", [createKpiCardItem()]));
      } else if (dragSource.type === "content-stat-grid") {
        copy.splice(index, 0, createStructureRow("1col", [createStatGridItem()]));
      } else if (dragSource.type === "content-image-caption") {
        copy.splice(index, 0, createStructureRow("1col", [createImageCaptionItem("main")]));
      }

      return copy;
    });

    setDragSource(null);
  };

  const addItemToCell = (rowId, cellId, itemFactory) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId || row.kind !== "structure") return row;

        const newCells = row.cells.map((cell) => {
          if (cell.id !== cellId) return cell;
          const items = [...(cell.items || []), itemFactory()];
          return { ...cell, items };
        });

        return { ...row, cells: newCells };
      })
    );
  };

  const updateItem = (itemId, patch) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.kind !== "structure") return row;
        const newCells = row.cells.map((cell) => {
          const items = (cell.items || []).map((it) =>
            it.id === itemId ? { ...it, ...patch } : it
          );
          return { ...cell, items };
        });
        return { ...row, cells: newCells };
      })
    );
  };

  // Upload an image for a specific imageKey. Stores a base64 data URL on the
  // workspace's IMDocument via the existing im_save_images endpoint, then
  // reflects it in local state so the editor updates without a refetch.
  const handleImageUpload = async (imageKey, dataUrl) => {
    if (!imageKey || !dataUrl) return;
    try {
      await saveIMImages(slug, { [imageKey]: dataUrl });
      setFull((prev) => {
        if (!prev) return prev;
        const pm = prev.pm || {};
        return {
          ...prev,
          pm: { ...pm, images: { ...(pm.images || {}), [imageKey]: dataUrl } },
        };
      });
    } catch (e) {
      console.error("Image upload failed", e);
      window.alert("Kunde inte spara bilden. Försök igen.");
    }
  };

  const handleCellDrop = (e, rowId, cellId) => {
    if (!dragSource) return;
    e.preventDefault();
    e.stopPropagation();

    if (dragSource.type === "im-section") {
      addItemToCell(rowId, cellId, () => createSectionItem(dragSource.sectionId));
    } else if (dragSource.type === "content-text") {
      addItemToCell(rowId, cellId, createTextItem);
    } else if (dragSource.type === "content-image") {
      addItemToCell(rowId, cellId, () => createImageItem("main"));
    } else if (dragSource.type === "content-divider") {
      addItemToCell(rowId, cellId, createInlineDividerItem);
    } else if (dragSource.type === "content-callout") {
      addItemToCell(rowId, cellId, () => createCalloutItem(dragSource.variant || "info"));
    } else if (dragSource.type === "content-quote") {
      addItemToCell(rowId, cellId, createQuoteItem);
    } else if (dragSource.type === "content-kpi") {
      addItemToCell(rowId, cellId, createKpiCardItem);
    } else if (dragSource.type === "content-stat-grid") {
      addItemToCell(rowId, cellId, createStatGridItem);
    } else if (dragSource.type === "content-image-caption") {
      addItemToCell(rowId, cellId, () => createImageCaptionItem("main"));
    }

    setDragSource(null);
  };

  const handleBeginDragSection = (sectionId) => {
    setDragSource({ type: "im-section", sectionId });
  };

  const handleEndDrag = () => {
    setDragSource(null);
  };

  const renderRowDropzone = (index) => (
    <div
      key={`drop-${index}`}
      className="pmstudio-row-dropzone"
      onDragOver={handleCanvasDragOver}
      onDrop={(e) => handleRowDrop(e, index)}
    />
  );

  const renderItem = (item, pm, rowId, cellId) => {
    const commonProps = { style: item.styles || {} };

    const handleClick = (e) => {
      e.stopPropagation();
      setSelectedTarget({ kind: "item", rowId, cellId, itemId: item.id });
    };

    const handleItemChange = (patch) => updateItem(item.id, patch);

    return (
      <div className="pmstudio-slot-item-inner" onClick={handleClick}>
        {item.type === "section" && <SectionBlock sectionId={item.sectionId} pm={pm} {...commonProps} />}
        {item.type === "text" && <TextBlock item={item} {...commonProps} />}
        {item.type === "image" && <ImageBlock pm={pm} imageKey={item.imageKey || "main"} item={item} onUpload={handleImageUpload} {...commonProps} />}
        {item.type === "inline-divider" && (
          <hr className="pmstudio-inline-divider" style={item.styles || {}} />
        )}
        {item.type === "callout" && (
          <CalloutBlock item={item} style={item.styles || {}} onChange={handleItemChange} />
        )}
        {item.type === "quote" && (
          <QuoteBlock item={item} style={item.styles || {}} onChange={handleItemChange} />
        )}
        {item.type === "kpi-card" && (
          <KpiCardBlock item={item} style={item.styles || {}} onChange={handleItemChange} />
        )}
        {item.type === "stat-grid" && (
          <StatGridBlock item={item} style={item.styles || {}} onChange={handleItemChange} />
        )}
        {item.type === "image-caption" && (
          <ImageCaptionBlock
            item={item}
            pm={pm}
            style={item.styles || {}}
            onChange={handleItemChange}
            onUpload={handleImageUpload}
          />
        )}
      </div>
    );
  };

  const renderRow = (row) => {
    if (row.kind === "divider") {
      return (
        <div key={row.id} className="pmstudio-block pmstudio-divider-row">
          <hr className="pmstudio-divider-line" />
        </div>
      );
    }

    if (row.kind === "pagebreak") {
      return (
        <div key={row.id} className="pmstudio-block pmstudio-pagebreak-row">
          <span className="pmstudio-pagebreak-label">Sidbrytning</span>
        </div>
      );
    }

    if (row.kind !== "structure") return null;

    const isSelected = selectedTarget?.kind === "row" && selectedTarget.rowId === row.id;

    const rowStyle = {
      ...(row.styles || {}),
      ...(row.bgColor && !row.styles?.backgroundColor ? { backgroundColor: row.bgColor } : {}),
    };

    return (
      <div
        key={row.id}
        className={"pmstudio-block pmstudio-structure-row" + (isSelected ? " pmstudio-block--selected" : "")}
        style={rowStyle}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedTarget({ kind: "row", rowId: row.id });
        }}
      >
        {isSelected && (
          <div className="pmstudio-block-flybar">
            <button type="button" className="pmstudio-block-flybar-btn" onClick={() => moveRow(row.id, "up")}>
              ↑
            </button>
            <button type="button" className="pmstudio-block-flybar-btn" onClick={() => moveRow(row.id, "down")}>
              ↓
            </button>
            <button
              type="button"
              className="pmstudio-block-flybar-btn pmstudio-block-flybar-btn--danger"
              onClick={() => deleteRow(row.id)}
            >
              ✕
            </button>
          </div>
        )}

        <div className="pmstudio-structure-inner">
          {row.layout === "1col" && (
            <div className="pmstudio-structure-cols pmstudio-structure-cols-1">
              {row.cells.map((cell) => (
                <div
                  key={cell.id}
                  className="pmstudio-slot"
                  onDragOver={handleCanvasDragOver}
                  onDrop={(e) => handleCellDrop(e, row.id, cell.id)}
                >
                  {(cell.items || []).length > 0 ? (
                    cell.items.map((item) => (
                      <div key={item.id} className="pmstudio-slot-item">
                        {renderItem(item, full?.pm, row.id, cell.id)}
                      </div>
                    ))
                  ) : (
                    <span className="pmstudio-slot-placeholder">Släpp innehåll här</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {row.layout === "1-2" && (
            <div className="pmstudio-structure-cols pmstudio-structure-cols-1-2">
              {row.cells.map((cell) => (
                <div
                  key={cell.id}
                  className="pmstudio-slot"
                  onDragOver={handleCanvasDragOver}
                  onDrop={(e) => handleCellDrop(e, row.id, cell.id)}
                >
                  {(cell.items || []).length > 0 ? (
                    cell.items.map((item) => (
                      <div key={item.id} className="pmstudio-slot-item">
                        {renderItem(item, full?.pm, row.id, cell.id)}
                      </div>
                    ))
                  ) : (
                    <span className="pmstudio-slot-placeholder">Släpp innehåll här</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const fontFamilies = [
    { value: "", label: "Standard" },
    { value: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif", label: "System" },
    { value: "Arial, Helvetica, sans-serif", label: "Arial" },
    { value: "'Helvetica Neue', Arial, sans-serif", label: "Helvetica Neue" },
    { value: "'Segoe UI', system-ui, sans-serif", label: "Segoe UI" },
    { value: "Roboto, system-ui, sans-serif", label: "Roboto" },
    { value: "Inter, system-ui, sans-serif", label: "Inter" },
    { value: "'Open Sans', system-ui, sans-serif", label: "Open Sans" },
    { value: "Lato, system-ui, sans-serif", label: "Lato" },
    { value: "Montserrat, system-ui, sans-serif", label: "Montserrat" },
    { value: "'Source Sans Pro', system-ui, sans-serif", label: "Source Sans" },
    { value: "Nunito, system-ui, sans-serif", label: "Nunito" },
    { value: "Georgia, 'Times New Roman', serif", label: "Georgia" },
    { value: "'Times New Roman', Times, serif", label: "Times New Roman" },
    { value: "Merriweather, Georgia, serif", label: "Merriweather" },
    { value: "'Playfair Display', Georgia, serif", label: "Playfair" },
    { value: "'Fira Sans', system-ui, sans-serif", label: "Fira Sans" },
    { value: "'IBM Plex Sans', system-ui, sans-serif", label: "IBM Plex" },
    { value: "'PT Sans', system-ui, sans-serif", label: "PT Sans" },
    { value: "Aptos, system-ui, sans-serif", label: "Aptos" },
  ];

  const fontSizes = [10, 11, 12, 14, 16, 18, 20, 24];

  const setAlign = (align) => applyStyleToSelected({ textAlign: align });

  const themeCssVars = themeToCssVars(theme);

  return (
    <div className="pmstudio-layout" style={themeCssVars}>
      <main className="pmstudio-center">
        <header className="pmstudio-editorbar">
          <div className="pmstudio-editorbar-left">
            <input
              className="pmstudio-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              aria-label="PM-titel"
            />
            <div className="pmstudio-sub">
              {(full?.company?.org_number || full?.organisation?.orgnr || slug || "—") + " • "}
              {(full?.company?.website || full?.organisation?.website || "—")}
            </div>
          </div>
        </header>

        <div className="pmstudio-global-toolbar">
          <select
            className="pmstudio-gt-select"
            value={activeStyles.fontFamily || ""}
            onChange={(e) => applyStyleToSelected({ fontFamily: e.target.value || undefined })}
            disabled={!selectedTarget}
          >
            {fontFamilies.map((f) => (
              <option key={f.label} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>

          <select
            className="pmstudio-gt-select"
            value={activeStyles.fontSize ? String(activeStyles.fontSize).replace("px", "") : ""}
            onChange={(e) => {
              const v = e.target.value;
              applyStyleToSelected({ fontSize: v ? `${v}px` : undefined });
            }}
            disabled={!selectedTarget}
          >
            <option value="">Storlek</option>
            {fontSizes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <span className="pmstudio-rt-sep" />

          <button
            type="button"
            className={"pmstudio-gt-btn" + (activeStyles.fontWeight === "700" ? " pmstudio-gt-btn--active" : "")}
            onClick={() =>
              applyStyleToSelected({ fontWeight: activeStyles.fontWeight === "700" ? undefined : "700" })
            }
            disabled={!selectedTarget}
          >
            B
          </button>
          <button
            type="button"
            className={"pmstudio-gt-btn" + (activeStyles.fontStyle === "italic" ? " pmstudio-gt-btn--active" : "")}
            onClick={() =>
              applyStyleToSelected({ fontStyle: activeStyles.fontStyle === "italic" ? undefined : "italic" })
            }
            disabled={!selectedTarget}
          >
            I
          </button>
          <button
            type="button"
            className={
              "pmstudio-gt-btn" + (activeStyles.textDecoration === "underline" ? " pmstudio-gt-btn--active" : "")
            }
            onClick={() =>
              applyStyleToSelected({
                textDecoration: activeStyles.textDecoration === "underline" ? undefined : "underline",
              })
            }
            disabled={!selectedTarget}
          >
            U
          </button>

          <span className="pmstudio-rt-sep" />

          <div className="pmstudio-gt-align-group">
            <button
              type="button"
              className={
                "pmstudio-gt-btn" +
                (activeStyles.textAlign === "left" || !activeStyles.textAlign ? " pmstudio-gt-btn--active" : "")
              }
              title="Vänsterjustera"
              onClick={() => setAlign("left")}
              disabled={!selectedTarget}
            >
              <span className="pmstudio-align-icon pmstudio-align-icon--left" aria-hidden="true" />
            </button>
            <button
              type="button"
              className={"pmstudio-gt-btn" + (activeStyles.textAlign === "center" ? " pmstudio-gt-btn--active" : "")}
              title="Centrera"
              onClick={() => setAlign("center")}
              disabled={!selectedTarget}
            >
              <span className="pmstudio-align-icon pmstudio-align-icon--center" aria-hidden="true" />
            </button>
            <button
              type="button"
              className={"pmstudio-gt-btn" + (activeStyles.textAlign === "right" ? " pmstudio-gt-btn--active" : "")}
              title="Högerjustera"
              onClick={() => setAlign("right")}
              disabled={!selectedTarget}
            >
              <span className="pmstudio-align-icon pmstudio-align-icon--right" aria-hidden="true" />
            </button>
            <button
              type="button"
              className={"pmstudio-gt-btn" + (activeStyles.textAlign === "justify" ? " pmstudio-gt-btn--active" : "")}
              title="Justera (block)"
              onClick={() => setAlign("justify")}
              disabled={!selectedTarget}
            >
              <span className="pmstudio-align-icon pmstudio-align-icon--justify" aria-hidden="true" />
            </button>
          </div>

          <span className="pmstudio-rt-sep" />

          <div className="pmstudio-gt-group">
            <span className="pmstudio-gt-label">Text</span>
            <button type="button" className="pmstudio-color-btn" disabled={!selectedTarget}>
              <input
                type="color"
                className="pmstudio-color-input-real"
                value={activeStyles.color || "#111827"}
                onChange={(e) => applyStyleToSelected({ color: e.target.value })}
              />
              <span className="pmstudio-color-dot" style={{ backgroundColor: activeStyles.color || "#111827" }} />
            </button>
          </div>

          <div className="pmstudio-gt-group">
            <span className="pmstudio-gt-label">Block</span>
            <button type="button" className="pmstudio-color-btn" disabled={!selectedTarget}>
              <input
                type="color"
                className="pmstudio-color-input-real"
                value={activeStyles.backgroundColor || "#ffffff"}
                onChange={(e) => applyStyleToSelected({ backgroundColor: e.target.value })}
              />
              <span
                className="pmstudio-color-dot"
                style={{ backgroundColor: activeStyles.backgroundColor || "#ffffff" }}
              />
            </button>
          </div>
        </div>

        {loading && <div className="pmstudio-loader">Laddar…</div>}
        {error && !loading && <div className="pmstudio-error">{error}</div>}

        {!loading && !error && full && (
          <div className="pmstudio-pages-shell">
            <div className="pmstudio-pages" ref={previewRef} onClick={() => setSelectedTarget(null)}>
              <Page>
                {renderRowDropzone(0)}
                {rows.map((row, index) => (
                  <React.Fragment key={row.id}>
                    {renderRow(row, index)}
                    {renderRowDropzone(index + 1)}
                  </React.Fragment>
                ))}
              </Page>
            </div>
          </div>
        )}
      </main>

      <aside className="pmstudio-right">
        <div className="pmstudio-right-inner">
          <div className="pmstudio-right-tabs">
            <button
              type="button"
              className={
                sidebarMode === "content"
                  ? "pmstudio-right-tab pmstudio-right-tab--active"
                  : "pmstudio-right-tab"
              }
              onClick={() => setSidebarMode("content")}
            >
              Content
            </button>
            <button
              type="button"
              className={
                sidebarMode === "design" ? "pmstudio-right-tab pmstudio-right-tab--active" : "pmstudio-right-tab"
              }
              onClick={() => setSidebarMode("design")}
            >
              Design
            </button>
          </div>

          {sidebarMode === "content" && (
            <>
              <div className="pmstudio-right-title">IM-texter</div>
              <p className="pmstudio-hint">
                Klicka för att redigera text. Dra en sektion in i dokumentet för att lägga till den som block.
              </p>

              <IMSectionsSidebar
                slug={slug}
                pm={full?.pm || {}}
                onSectionChange={handleSectionChange}
                onBeginDragSection={handleBeginDragSection}
                onEndDrag={handleEndDrag}
              />
            </>
          )}

          {sidebarMode === "design" && (
            <div className="pmstudio-sections-accordion">
              <div className="pmstudio-sec-item">
                <div className="pmstudio-sec-header">
                  <div className="pmstudio-sec-header-text">
                    <div className="pmstudio-sec-title">Strukturblock</div>
                    <div className="pmstudio-sec-subtitle">
                      Rader som styr layouten, likt Mailchimp (1 kolumn, 1:2 kolumn). Dra in i dokumentet.
                    </div>
                  </div>
                </div>
                <div className="pmstudio-sec-body">
                  <div className="pmstudio-struct-palette">
                    <div
                      className="pmstudio-struct-card pmstudio-struct-card--1col"
                      draggable
                      onDragStart={() => setDragSource({ type: "structure-1col" })}
                      onDragEnd={handleEndDrag}
                    >
                      <div className="pmstudio-struct-thumb pmstudio-struct-thumb--1col" />
                      <div className="pmstudio-struct-label">1 Column</div>
                    </div>

                    <div
                      className="pmstudio-struct-card pmstudio-struct-card--1-2"
                      draggable
                      onDragStart={() => setDragSource({ type: "structure-1-2" })}
                      onDragEnd={handleEndDrag}
                    >
                      <div className="pmstudio-struct-thumb pmstudio-struct-thumb--1-2" />
                      <div className="pmstudio-struct-label">1:2 Column</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pmstudio-sec-item">
                <div className="pmstudio-sec-header">
                  <div className="pmstudio-sec-header-text">
                    <div className="pmstudio-sec-title">Innehållsblock</div>
                    <div className="pmstudio-sec-subtitle">Dra in text-, bildblock, linje eller sidbrytning.</div>
                  </div>
                </div>
                <div className="pmstudio-sec-body">
                  <div className="pmstudio-content-palette">
                    <button
                      type="button"
                      className="pmstudio-content-pill"
                      draggable
                      onDragStart={() => setDragSource({ type: "content-text" })}
                      onDragEnd={handleEndDrag}
                    >
                      Textblock
                    </button>
                    <button
                      type="button"
                      className="pmstudio-content-pill"
                      draggable
                      onDragStart={() => setDragSource({ type: "content-image" })}
                      onDragEnd={handleEndDrag}
                    >
                      Bildblock
                    </button>
                    <button
                      type="button"
                      className="pmstudio-content-pill"
                      draggable
                      onDragStart={() => setDragSource({ type: "content-divider" })}
                      onDragEnd={handleEndDrag}
                    >
                      Divider / linje
                    </button>
                    <button
                      type="button"
                      className="pmstudio-content-pill"
                      draggable
                      onDragStart={() => setDragSource({ type: "content-pagebreak" })}
                      onDragEnd={handleEndDrag}
                    >
                      Sidbrytning
                    </button>
                  </div>
                </div>
              </div>

              <div className="pmstudio-sec-item">
                <div className="pmstudio-sec-header">
                  <div className="pmstudio-sec-header-text">
                    <div className="pmstudio-sec-title">Visuella block</div>
                    <div className="pmstudio-sec-subtitle">
                      Stilade element som lyfter PM:et. Använd temats färger.
                    </div>
                  </div>
                </div>
                <div className="pmstudio-sec-body">
                  <div className="pmstudio-content-palette">
                    <button
                      type="button"
                      className="pmstudio-content-pill"
                      draggable
                      onDragStart={() => setDragSource({ type: "content-callout", variant: "info" })}
                      onDragEnd={handleEndDrag}
                    >
                      Callout (info)
                    </button>
                    <button
                      type="button"
                      className="pmstudio-content-pill"
                      draggable
                      onDragStart={() => setDragSource({ type: "content-callout", variant: "accent" })}
                      onDragEnd={handleEndDrag}
                    >
                      Callout (accent)
                    </button>
                    <button
                      type="button"
                      className="pmstudio-content-pill"
                      draggable
                      onDragStart={() => setDragSource({ type: "content-quote" })}
                      onDragEnd={handleEndDrag}
                    >
                      Citat
                    </button>
                    <button
                      type="button"
                      className="pmstudio-content-pill"
                      draggable
                      onDragStart={() => setDragSource({ type: "content-kpi" })}
                      onDragEnd={handleEndDrag}
                    >
                      KPI-kort
                    </button>
                    <button
                      type="button"
                      className="pmstudio-content-pill"
                      draggable
                      onDragStart={() => setDragSource({ type: "content-stat-grid" })}
                      onDragEnd={handleEndDrag}
                    >
                      KPI-rad (3 kort)
                    </button>
                    <button
                      type="button"
                      className="pmstudio-content-pill"
                      draggable
                      onDragStart={() => setDragSource({ type: "content-image-caption" })}
                      onDragEnd={handleEndDrag}
                    >
                      Bild med bildtext
                    </button>
                  </div>
                </div>
              </div>

              {selectedRow && (
                <div className="pmstudio-sec-item">
                  <div className="pmstudio-sec-header">
                    <div className="pmstudio-sec-header-text">
                      <div className="pmstudio-sec-title">Valt block</div>
                      <div className="pmstudio-sec-subtitle">Bakgrundsfärg för markerad rad</div>
                    </div>
                  </div>
                  <div className="pmstudio-sec-body">
                    <label className="pmstudio-sec-label">Bakgrundsfärg</label>
                    <input
                      type="color"
                      value={selectedRow.styles?.backgroundColor || "#ffffff"}
                      onChange={(e) => applyStyleToSelected({ backgroundColor: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

/* ======================================================================= */
/* OUTER PAGE – FETCH & SPARA MALL                                         */
/* ======================================================================= */

function PMStudioEditorPage() {
  const { company: slug } = useParams();
  const location = useLocation();

  const selectedTemplate = location.state?.template || null;

  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [title, setTitle] = useState(selectedTemplate?.title || "Investment memorandum");
  const [rows, setRows] = useState([]);
  const [theme, setTheme] = useState(selectedTemplate?.theme || {});

  const [savingTemplate, setSavingTemplate] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const data = await fetchIMFull(slug);
        if (!alive) return;

        setFull(data);

        const templateRows = Array.isArray(selectedTemplate?.layout) ? selectedTemplate.layout : [];
        setRows(composePresentationRows(templateRows, data?.pm?.sections || {}));

        if (selectedTemplate?.theme) {
          setTheme(selectedTemplate.theme);
        }
      } catch (e) {
        if (!alive) return;
        console.error("PMStudioEditorPage: failed to load IM data", e);
        setError("Kunde inte ladda bolagsdata.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [slug, selectedTemplate]);

  const handleSaveAsTemplate = async () => {
    const name = window.prompt("Namn på mall:", selectedTemplate?.name || "IM-mall");
    if (!name) return;

    const saveGlobal = window.confirm(
      "Spara som företagsmall (återanvändbar för alla kunder)?\n\nOK = företagsmall\nAvbryt = endast för den här kunden"
    );

    setSavingTemplate(true);
    setSaveError("");
    setSaveSuccess("");

    const payload = {
      name,
      title,
      layout: rows,
      theme,
    };

    try {
      if (saveGlobal) {
        await saveGlobalIMTemplate(payload);
        setSaveSuccess("Mallen är sparad som företagsmall (utan starter-text — sätt via mallhanteringen).");
      } else {
        await saveIMTemplate(slug, payload);
        setSaveSuccess("Mallen är sparad för den här kunden.");
      }
    } catch (e) {
      console.error("Failed to save IM template", e);
      setSaveError("Kunde inte spara mallen. Försök igen om en stund.");
    } finally {
      setSavingTemplate(false);
    }
  };

  return (
    <Main navItems={makeCompanyNav(slug)}>
      <div className="pmstudio-root">
        <header className="pm-template-editor-header">
          <div className="pm-template-editor-header-left">
            <h1 className="pm-template-editor-title">{selectedTemplate ? selectedTemplate.name : "Ny IM-mall"}</h1>
            <p className="pm-template-editor-sub">
              Bygg och justera IM-layouten. Innehåll hämtas från era sparade IM-texter i databasen.
            </p>
          </div>

          <div className="pm-template-editor-header-actions">
            {saveError && (
              <span className="pm-template-editor-pill pm-template-editor-pill--error">{saveError}</span>
            )}
            {saveSuccess && (
              <span className="pm-template-editor-pill pm-template-editor-pill--ok">{saveSuccess}</span>
            )}

            <button
              type="button"
              className="pm-template-editor-save-btn"
              onClick={handleSaveAsTemplate}
              disabled={savingTemplate}
            >
              {savingTemplate ? "Sparar…" : "Spara presentationsmall"}
            </button>
          </div>
        </header>

        <PMStudioEditorInner
          slug={slug}
          full={full}
          setFull={setFull}
          loading={loading}
          error={error}
          title={title}
          setTitle={setTitle}
          rows={rows}
          setRows={setRows}
          theme={theme}
          setTheme={setTheme}
        />
      </div>
    </Main>
  );
}

export default PMStudioEditorPage;
